import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, take } from 'rxjs/operators';
import { I18nService } from 'i18n';
import { Logger } from 'shared';
import { AuthService } from '../services/auth.service';

/**
 * Blocks the dashboard until the customer has acknowledged the legal documents their
 * branding domain requires.
 *
 * Sits on the DefaultLayoutComponent wrapper alongside emailVerifiedGuard, so it
 * covers every route inside the layout at once. The gate page itself is mounted a
 * level up (authenticated, outside the layout) — putting it inside would place it
 * behind this guard and loop.
 *
 * Reads the flag from the user in auth state rather than calling the API: guards
 * cannot await, and the flag rides on the bootstrap payload that has already resolved
 * before initial navigation is enabled.
 *
 * Fails open by design. Only `legal_acknowledgement_required === true` blocks, so an
 * app whose backend does not implement the Legal & Compliance Center (and therefore
 * omits the field) is unaffected. Enforcement does not rest on this guard: the gate is
 * a UX affordance, and it is the backend that decides what is outstanding.
 */
export const legalAcknowledgementGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const i18nService = inject(I18nService);
  const logger = new Logger('legalAcknowledgementGuard');

  return authService.getUser$().pipe(
    take(1),
    map(user => {
      if (!user) {
        logger.debug('NO USER => REDIRECT TO /login');
        return router.createUrlTree(['/login']);
      }

      if (user.legal_acknowledgement_required !== true) {
        return true;
      }

      logger.debug(`ACKNOWLEDGEMENT OUTSTANDING => GATE (from ${state.url})`);
      return router.createUrlTree([`/${i18nService.getLanguage()}/legal/acknowledge`]);
    })
  );
};
